import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, CheckCircle, XCircle } from "lucide-react";

const API = `${window.location.origin}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const isAccademiaWelcome = searchParams.get("welcome") === "accademia";
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    
    setLoading(true);
    
    try {
      // 1. Reimposta la password
      const resetRes = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });

      // 2. Tentiamo l'auto-login per non far ridigitare le credenziali al cliente
      const userEmail = resetRes.data?.email;
      if (userEmail) {
        try {
          const loginRes = await axios.post(`${API}/auth/login`, {
            email: userEmail,
            password,
          });
          if (loginRes.data?.token) {
            localStorage.setItem("token", loginRes.data.token);
            const role = loginRes.data?.user?.role;
            toast.success("Benvenuto/a! Accesso effettuato.");
            // Cliente Accademia → vai dritto alla Biblioteca
            const dest = role === "cliente_accademia" || isAccademiaWelcome
              ? "/biblioteca"
              : "/";
            // Hard redirect per garantire che AuthContext si aggiorni leggendo localStorage
            window.location.href = dest;
            return;
          }
        } catch (loginErr) {
          // Auto-login fallito: passa al fallback (success page con bottone)
          console.warn("Auto-login non riuscito, passo al login manuale:", loginErr);
        }
      }

      // Fallback: mostra schermata di successo con bottone "Vai al Login"
      setSuccess(true);
      toast.success("Password reimpostata con successo!");
    } catch (err) {
      const message = err.response?.data?.detail || "Errore nel reset della password";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
        <Card className="rounded-2xl shadow-xl border-border/50 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-serif text-xl font-semibold mb-2">Link Non Valido</h2>
            <p className="text-muted-foreground mb-6">
              Il link di reset non è valido. Richiedi un nuovo link di reset password.
            </p>
            <Link to="/forgot-password">
              <Button className="rounded-full">
                Richiedi Nuovo Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
        <Card className="rounded-2xl shadow-xl border-border/50 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-serif text-xl font-semibold mb-2">Password Reimpostata!</h2>
            <p className="text-muted-foreground mb-6">
              La tua password è stata cambiata con successo. Ora puoi accedere con la nuova password.
            </p>
            <Link to="/login">
              <Button className="rounded-full btn-shine">
                Vai al Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center p-4">
        <Card className="rounded-2xl shadow-xl border-border/50 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-serif text-xl font-semibold mb-2">Errore</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/forgot-password">
              <Button className="rounded-full">
                Richiedi Nuovo Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src={isAccademiaWelcome
              ? "/odr-assets/logo_odr_accademia.jpg"
              : "https://customer-assets.emergentagent.com/job_beauty-wellness-31/artifacts/42tsb1mv_PdfImage.png"}
            alt="Accademia ODR ITA 318"
            className="h-24 mx-auto mb-4 object-contain"
          />
          <h1 className="font-serif text-2xl font-semibold text-[#B87A8A]">
            {isAccademiaWelcome ? "Benvenuto/a in Accademia" : "La Bellezza che Cura"}
          </h1>
          {isAccademiaWelcome && (
            <p className="text-sm text-gray-500 mt-2 italic">
              Imposta la tua password per accedere a video, podcast e dispense
            </p>
          )}
        </div>

        <Card className="rounded-2xl shadow-xl border-border/50">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-serif text-2xl">
              {isAccademiaWelcome ? "Imposta la tua password" : "Nuova Password"}
            </CardTitle>
            <CardDescription>
              {isAccademiaWelcome
                ? "Scegli una password sicura per il tuo account Accademia ODR"
                : "Scegli una nuova password per il tuo account"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nuova Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimo 6 caratteri"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="rounded-xl pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Ripeti la password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="rounded-xl"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-full btn-shine"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Salvataggio...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4" />
                    Salva Nuova Password
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
